/**
 * Trip DAG: build normalized node/edge list from route structure for storage and LP.
 * When source and destination are the same storage location, they are distinct nodes
 * (storage:source:id and storage:dest:id) to avoid a cycle in the graph.
 */

import toposort from 'toposort';
import { getSegmentNodeId, type PathSegment, type RouteStructure } from './routeInference';
import { invariant } from './invariant';
import { dagSourceNodeId, dagDestNodeId } from './tripPlanningConstants';

export type TripDagNodeKind = 'source' | 'event' | 'dest';

export interface TripDagNode {
    index: number;
    kind: TripDagNodeKind;
    eventId?: string;
    storageLocationId?: string;
}

export interface TripDagEdge {
    fromNodeIndex: number;
    toNodeIndex: number;
}

export interface TripDag {
    nodes: TripDagNode[];
    edges: TripDagEdge[];
}

/** Node id for DAG: differentiate source vs dest when same storage is used for both. Exported for supply flow leg mapping. */
export function getSegmentNodeIdForDag(seg: PathSegment, segmentIndex: number, pathLength: number): string {
    if (seg.kind === 'storage') {
        if (segmentIndex === 0) return dagSourceNodeId(seg.storageLocationId);
        if (segmentIndex === pathLength - 1) return dagDestNodeId(seg.storageLocationId);
    }
    return getSegmentNodeId(seg);
}

/**
 * Build a single DAG (nodes + edges) from route structure and source/dest storage.
 * Nodes: index 0 = source, indices 1..N-2 = events in topological order, index N-1 = dest.
 * Edges: deduplicated (fromNodeIndex, toNodeIndex) from consecutive path segments.
 */
export function buildTripDagFromRouteStructure(
    routeStructure: RouteStructure,
    sourceStorageLocationId: string,
    destStorageLocationId: string
): TripDag {
    const sourceId = dagSourceNodeId(sourceStorageLocationId);
    const destId = dagDestNodeId(destStorageLocationId);

    const nodeIds = new Set<string>();
    const edgePairs = new Set<string>(); // "fromId\toId" for dedup

    for (const path of Array.from(routeStructure.trailerPaths.values())) {
        const pathLength = path.segments.length;
        for (let i = 0; i < pathLength; i++) {
            const seg: PathSegment = path.segments[i];
            const id = getSegmentNodeIdForDag(seg, i, pathLength);
            nodeIds.add(id);
            if (i + 1 < pathLength) {
                const nextId = getSegmentNodeIdForDag(path.segments[i + 1], i + 1, pathLength);
                edgePairs.add(`${id}\t${nextId}`);
            }
        }
    }

    if (!nodeIds.has(sourceId) || !nodeIds.has(destId)) {
        return { nodes: [], edges: [] };
    }

    const edgePairsArr: string[] = [];
    for (const s of Array.from(edgePairs)) edgePairsArr.push(s);
    const edgesById: Array<[string, string]> = edgePairsArr.map(s => {
        const parts = s.split('\t');
        const a = parts[0];
        const b = parts[1];
        if (a === undefined || b === undefined) throw new Error('tripDag: invalid edge pair encoding');
        return [a, b];
    });

    const allNodes: string[] = [];
    for (const id of Array.from(nodeIds)) allNodes.push(id);
    let order: string[] = toposort.array(allNodes, edgesById);

    if (order.length > 1 && order[order.length - 1] !== destId) {
        const destIdx = order.indexOf(destId);
        if (destIdx >= 0) {
            order = order.filter(id => id !== destId);
            order.push(destId);
        }
    }
    if (order.length > 1 && order[0] !== sourceId) {
        const sourceIdx = order.indexOf(sourceId);
        if (sourceIdx >= 0) {
            order = order.filter(id => id !== sourceId);
            order.unshift(sourceId);
        }
    }

    invariant(order[0] === sourceId, `DAG node 0 must be source (got ${order[0]})`);
    invariant(order[order.length - 1] === destId, `DAG last node must be dest (got ${order[order.length - 1]})`);

    const nodeIdToIndex = new Map<string, number>();
    order.forEach((id, i) => nodeIdToIndex.set(id, i));

    const nodes: TripDagNode[] = order.map((id, index) => {
        if (id === sourceId) {
            return { index, kind: 'source' as const, storageLocationId: sourceStorageLocationId };
        }
        if (id === destId) {
            return { index, kind: 'dest' as const, storageLocationId: destStorageLocationId };
        }
        return { index, kind: 'event' as const, eventId: id };
    });

    const edgeSet = new Set<string>();
    const edges: TripDagEdge[] = [];
    for (const [fromId, toId] of edgesById) {
        const fi = nodeIdToIndex.get(fromId);
        const ti = nodeIdToIndex.get(toId);
        if (fi === undefined || ti === undefined) continue;
        const key = `${fi}\t${ti}`;
        if (edgeSet.has(key)) continue;
        edgeSet.add(key);
        edges.push({ fromNodeIndex: fi, toNodeIndex: ti });
    }

    return { nodes, edges };
}
